import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Typography,
  Heading,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

import { useDfiAnnualReportPdf } from 'api';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { PageHeading } from 'components';
import { KpiAccordion, StaticReviewCard } from './components';
import { DfiReportResponse } from 'types';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

import { useFormattedDfiReport } from './hooks';

interface CompleteDfiReportProps {
  heading: string;
  report: DfiReportResponse;
}

export const CompleteDfiReport: React.FC<CompleteDfiReportProps> = ({ heading, report }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('annual_dfi_report_complete');
  const { isTQ } = useThemedValueSwitch();

  const { mutate: downloadReportPdf, isLoading } = useDfiAnnualReportPdf(
    isTQ
      ? `${t('tqen_annual_dfi_report', 'Annual Report')} ${report.reportYear}`
      : `${t('annual_dfi_report', 'Annual Dfi Report')} ${report.reportYear}`
  );

  const { titles, metrics: kpiData } = useFormattedDfiReport(report);

  const formatLocaleDate = useFormatLocaleDate();

  return (
    <>
      <Grid gridGap={5}>
        <PageHeading heading={heading} />
        <Flex
          justifyContent='flex-end'
          alignItems='center'
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Button
            isLoading={isLoading}
            disabled={!report.path}
            size='sm'
            variant='secondary'
            w={isMobile ? '100%' : 'inherit'}
            onClick={downloadReportPdf}
          >
            {t('download_report', 'Download report')}
          </Button>
        </Flex>
      </Grid>
      <KpiAccordion kpiData={kpiData} titles={titles} />
      <Divider my={8} borderColor={COLOURS.gray[300]} />
      <Heading size='lg'>{t('review', 'Review')}</Heading>
      <Typography>
        <tqc.p>
          {t(
            'review_sub_heading_text',
            'Consultees can use the report to highlight to the Department any initiatives or practices that may improve the consultation process. This may be a consultee committing to providing guidance or standing advice for use by planning officers that will negate the need for a consultation or it may propose an approach to dealing with consultations within its own organisation in a more efficient manner.'
          )}
        </tqc.p>
      </Typography>
      <Grid gridGap={5} mt={4}>
        <GridItem>
          <StaticReviewCard
            question={t(
              'dfi_review_question_1',
              'Please highlight below any initiatives or best practices you have developed which have improved your consultation process and could be shared with other consultees to make the consultation process more streamlined for all.'
            )}
            response={report.bestPracticeResponse}
          />
        </GridItem>
        <GridItem>
          <StaticReviewCard
            question={t(
              'dfi_review_question_2',
              'Please highlight any issues that you have had with the consultation process which you feel have hampered your ability to respond to consultation requests.'
            )}
            response={report.bestPracticeResponse}
          />
        </GridItem>
      </Grid>
      <Divider my={8} borderColor={COLOURS.gray[300]} />
      <Typography color={COLOURS.gray[800]}>
        <tqc.p fontSize={'0.875rem !important'}>{`${t(
          'report_completed_on',
          'Report completed on'
        )} ${formatLocaleDate(report.dateCompleted)}`}</tqc.p>
      </Typography>
    </>
  );
};
